import api from "./api";
import { resolveRoleName } from "../utils/auth";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

// Normalize various backend shapes to a consistent person record
export const normalizePerson = (dto) => ({
  people_id: dto?.people_id ?? dto?.id,
  company_id: dto?.company_id ?? null,
  job_id: dto?.job_id ?? null,
  is_manager: Boolean(dto?.is_manager),
  people_name: dto?.people_name ?? dto?.name ?? "",
  people_surname: dto?.people_surname ?? dto?.surname ?? "",
  people_email: dto?.people_email ?? dto?.email ?? "",
  people_phone: dto?.people_phone ?? dto?.phone ?? "",
  created_at: dto?.created_at ?? dto?.createdAt ?? null,
  updated_at: dto?.updated_at ?? dto?.updatedAt ?? null,
});

const extractPeopleArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeRoleKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

const getCurrentAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  try {
    const raw = localStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    if (auth?.user) return auth.user;
  } catch {}

  return null;
};

async function getCurrentUserRoleKey() {
  const user = getCurrentAuthUser();
  if (!user) return "";
  const roleName = await resolveRoleName(user);
  return normalizeRoleKey(roleName);
}

const toQuery = ({ page, limit, search } = {}) => {
  const query = new URLSearchParams();
  if (page != null) query.append("page", String(page));
  if (limit != null) query.append("limit", String(limit));
  if (String(search || "").trim()) {
    query.append("search", String(search).trim());
  }
  return query.toString();
};

const extractMeta = (payload, page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) => {
  if (payload?.meta && typeof payload.meta === "object") {
    return {
      total: Number(payload.meta.total) || 0,
      page: Number(payload.meta.page) || Number(page) || DEFAULT_PAGE,
      limit: Number(payload.meta.limit) || Number(limit) || DEFAULT_LIMIT,
      totalPages: Number(payload.meta.totalPages) || 1,
    };
  }
  const data = extractPeopleArray(payload);
  return {
    total: data.length,
    page: Number(page) || DEFAULT_PAGE,
    limit: Number(limit) || DEFAULT_LIMIT,
    totalPages: 1,
  };
};

export const getPeople = async () => {
  const res = await api.get("/people");
  return extractPeopleArray(res).map(normalizePerson);
};

export async function getPeoplePaginated({
  page = DEFAULT_PAGE,
  limit = DEFAULT_LIMIT,
  search = "",
} = {}) {
  const qs = toQuery({ page, limit, search });
  const roleKey = await getCurrentUserRoleKey();
  const endpoint =
    roleKey === "SUPERADMIN" ? "/people" : (
      "/hierarchical-view/relation/people/bulk"
    );
  const res = await api.get(`${endpoint}${qs ? `?${qs}` : ""}`);

  const data = extractPeopleArray(res).map(normalizePerson);
  return {
    data,
    meta: extractMeta(res, page, limit),
  };
}

export const getPersonById = async (id) => {
  if (id == null) throw new Error("id is required");
  return await api.get(`/people/${id}`);
};

export const createPerson = async (payload) => {
  // expects: { name, surname, email, phone, is_manager, company_id, job_id }
  return await api.post("/people", payload);
};

export const updatePerson = async (id, payload) => {
  if (id == null) throw new Error("id is required");
  return await api.patch(`/people/${id}`, payload);
};

export const deletePerson = async (id) => {
  if (id == null) throw new Error("id is required");
  return await api.delete(`/people/${id}`);
};

// POST /hierarchical-view/relation/people/bulk
// Body: { userIds: number[] }
// Returns de-duplicated normalized list of people from associatedPeople + createdPeople
export async function getPeopleByUsersBulk(userIds = []) {
  // SUPER_ADMIN should use full list
  try {
    const raw = sessionStorage.getItem("auth");
    const auth = raw ? JSON.parse(raw) : null;
    const currentUser = auth?.user;
    if (currentUser) {
      const rn = await resolveRoleName(currentUser);
      const norm = String(rn || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
      if (norm === "SUPERADMIN") {
        const res = await getPeople();
        return Array.isArray(res) ? res.map(normalizePerson) : [];
      }
    }
  } catch {}
  const ids =
    Array.isArray(userIds) ?
      userIds.map((n) => Number(n)).filter(Number.isInteger)
    : [];
  if (ids.length === 0) return [];
  const key = ids
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  if (!getPeopleByUsersBulk._inflight)
    getPeopleByUsersBulk._inflight = new Map();
  if (getPeopleByUsersBulk._inflight.has(key))
    return await getPeopleByUsersBulk._inflight.get(key);
  const payload = { userIds: ids };
  const inflight = (async () => {
    const res = await api.post(
      "/hierarchical-view/relation/people/bulk",
      payload,
    );
    const results =
      Array.isArray(res?.results) ? res.results
      : Array.isArray(res) ? res
      : [];
    const coll = [];
    for (const r of results) {
      const data = r?.data || {};
      const assoc =
        Array.isArray(data.associatedPeople) ? data.associatedPeople : [];
      const created =
        Array.isArray(data.createdPeople) ? data.createdPeople : [];
      coll.push(...assoc, ...created);
    }
    const byId = new Map();
    coll.forEach((p) => {
      const np = normalizePerson(p);
      const k = Number(np.people_id);
      if (Number.isInteger(k) && !byId.has(k)) byId.set(k, np);
    });
    return Array.from(byId.values());
  })().finally(() => {
    getPeopleByUsersBulk._inflight.delete(key);
  });
  getPeopleByUsersBulk._inflight.set(key, inflight);
  return inflight;
}

const peopleService = {
  getPeople,
  getPeoplePaginated,
  getPersonById,
  createPerson,
  updatePerson,
  deletePerson,
  getPeopleByUsersBulk,
};

export default peopleService;
